import { ThemeProvider, createTheme } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux';
import { selectUser } from '../Redux/userSlice';
import { MRT_ColumnDef, MaterialReactTable, useMaterialReactTable } from 'material-react-table'
import { IoEye } from 'react-icons/io5';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import { AlertType, bgLineGridColorDark, bgLineGridColorLight, ContatoCliente, corBgGrid, NFFilter, RetAPI } from '../../Utils/Types';
import { RiEdit2Fill } from 'react-icons/ri';
import { FaCheck, FaTrashAlt } from 'react-icons/fa';
import { BiLockOpenAlt } from 'react-icons/bi';
import moment from 'moment';
import { Tooltip } from 'react-tooltip'
import { Funcoes } from '../../Utils/Funcoes';
import { NotaEntradaService } from '../../Pages/NotasEntrada/NotaEntradaService';
import { ModalExcluirDados } from '../Modal/ModalExcluirDados';

interface RowData {
    id: number,
    dt_emissao: string,
    dt_chegada: string,
    id_fornecedor: number,
    id_empresa: number,
    indPag: number,
    vl_desconto: number,
    vl_frete: number,
    vl_outras: number,
    vl_seguro: number,
    vl_produtos: number,
    vl_ipi: number,
    vl_nf: number,
    vl_bc_icms: number,
    v_icms: number,
    vl_bc_st: number,
    vl_st: number,
    vl_pis: number,
    vl_cofins: number,
    id_transp: number,
    modelo: number,
    serie: number,
    num_nf: number,
    fin_nfe: number,
    id_nat_op: number,
    id_user_insert: number,
    dt_cadastro: string,
    id_user_last_update: number,
    dh_last_update: string,
    dh_confirm_nf: string,
    id_user_confirm_nf: number,
    nome_fantasia: string,
    name_user_insert: string
}

type Relatorio = {
    datai: string;
    dataf: string;
    idFornecedorFilter: number;
    status: string;
}

type Props = {
    NFList: NFFilter[],
    confirmarNota: (id: number) => Promise<boolean>,
    buscarDadosNF: (id: number) => Promise<boolean>,    
    visualizarNF: (id: number) => Promise<boolean>,
    habilitarNF: (id: number) => Promise<boolean>,
    tipo: TipoStatus,
    relatorio: Relatorio
}

type VisibilityState = Record<string, boolean>

export type TipoStatus = | "C" | "N"

export const GridEntradaNota = React.memo(({ NFList, confirmarNota, buscarDadosNF, visualizarNF, habilitarNF, tipo, relatorio }: Props) => {

    const defaultJson = useMemo(() => {
        return (
            {
                id: true,
                num_nf: true,
                nome_fantasia: true,
                dt_emissao: true,
                dt_chegada: true,
                modelo: true,
                vl_produtos: true,
                vl_nf: true,
            }
        )
    }, [])

    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(defaultJson)
    const [nfList, setNfList]                     = useState<NFFilter[]>([])
    const [isLoading, setIsLoading]               = useState(false)
    const [modalShow, setModalShow]               = useState(false)
    const [numNF, setNumNF]                       = useState(0)
    const [idNF, setIdNF]                         = useState(0)
    const [feedBackVisible, setFeedBackVisible]   = useState(false)
    const [messageFeedBack, setMessageFeedBack]   = useState("")
    const [alertType, setAlertType]               = useState<AlertType>("SUCCESS")
    let user                                      = useSelector(state => selectUser(state));

    useEffect(() => {
        setNfList(NFList)
    }, [NFList])

    useEffect(() => {
        setNfList([])
        const timer = setTimeout(() => {
            const newColumnVisibility = { ...columnVisibility }
            newColumnVisibility.acaoNfNaoConfirmada = tipo === "N"
            newColumnVisibility.acaoNfConfirmada = tipo === "C"

            setColumnVisibility(newColumnVisibility)
            clearTimeout(timer);
        }, 150);

    }, [tipo])

    const atualizarNfList = async () => {
        const retAPI = await NotaEntradaService.FiltrarNf(
            relatorio.datai, 
            relatorio.dataf, 
            relatorio.idFornecedorFilter, 
            relatorio.status
        );
        setNfList(retAPI.data);
    };

    const theme = createTheme({
        palette: {
            mode: user.modeDark,

            background: {
                default:
                    user.modeDark === 'light' ? '#fff' : '#475569',
            },
        },
    })

    const handleExcluir = useCallback(async (motivo: string, senha: string) => {
    
            setFeedBackVisible(false)
            setIsLoading(true)
    
            try {
    
                const retApi: RetAPI = await NotaEntradaService.ExcluirNF(idNF, motivo, senha)
                
    
                if (!retApi.error) {
                    setFeedBackVisible(true)
                    setAlertType("SUCCESS")
                    setMessageFeedBack(`Grupo excluído com sucesso`)                                                    
                } else {
                    setFeedBackVisible(true)
                    setAlertType("DANGER")
                    setMessageFeedBack(retApi.msg || ``)
                    setIsLoading(false)                    
                }
    
            } catch (error) {
                setIsLoading(false)
                setFeedBackVisible(true)
                setAlertType("DANGER")
                setMessageFeedBack(`Não foi possível excluir o grupo. Por favor, tente novamente`)
            }
    
        }, [])

    const fecharModalExcluirDados = useCallback(() => {
            setIsLoading(false)
            setFeedBackVisible(false)
            
            if (alertType === 'SUCCESS') {
                setModalShow(false)
                setNumNF(0)
            }
        }, [alertType])
    
        const buttonFecharModalExcluirDados = useCallback(() => {
            setIsLoading(false)
            setFeedBackVisible(false)
            setModalShow(false)
            setNumNF(0)
        }, [])

    const baseBackgroundColor = user.modeDark === 'dark' ? '#475569' : '#fff'

    const columns = useMemo<MRT_ColumnDef<NFFilter>[]>(() => [

        {
            accessorKey: 'acaoNfConfirmada', header: 'Ações', enableSorting: false, size: 100, muiTableBodyCellProps: { align: 'left' }, muiTableHeadCellProps: { align: 'left' },

            Cell: ((cell: any) => {

                const rowData = cell.row.original as RowData;

                const handleVisualizarNFClick = async () => {
                    const result = await visualizarNF(rowData.id)

                    if (result) {
                        await atualizarNfList();
                    }
                }

                const handleHabilitarNFClick = async () => {
                    const result = await habilitarNF(rowData.id)

                    if (result) {
                        await atualizarNfList();
                    }
                }

                return (
                    <div className='flex justify-start w-full'>

                        <button
                            tabIndex={-1}
                            onClick={handleVisualizarNFClick} data-tooltip-id={'tooltip-visualizarNF-' + rowData.id} id={'btn-visualizarNF-' + rowData.id}
                            className={`flex items-center justify-center 
                                        text-white transition-all duration-300 
                                        hover:bg-slate-800 w-[30px] h-[30px] rounded-md 
                                        bg-slate-400`}>
                            <IoEye size={'18px'} />
                        </button>
                        <Tooltip id={`tooltip-visualizarNF-${rowData.id}`} anchorSelect={'#btn-visualizarNF-' + rowData.id} style={{ backgroundColor: 'rgb(30, 41, 59)' }} place='top' positionStrategy="fixed">
                            <div className={`flex items-center`}>
                                <span className={`font-sans font-medium text-[13px]`}>Visualizar NF</span>
                            </div>
                        </Tooltip>

                        <button
                            tabIndex={-1}
                            onClick={handleHabilitarNFClick} data-tooltip-id={'tooltip-habilitar-' + rowData.id} id={'btn-habilitar-' + rowData.id}
                            className={`flex items-center justify-center 
                                        text-white transition-all duration-300 
                                        hover:bg-indigo-600 w-[30px] h-[30px] rounded-md 
                                        bg-indigo-500 ml-2`} >
                            <BiLockOpenAlt size="18px" />
                        </button>
                        <Tooltip id={`tooltip-habilitar-${rowData.id}`} anchorSelect={'#btn-habilitar-' + rowData.id} style={{ backgroundColor: 'rgb(99, 102, 241)' }} place='top-end' positionStrategy="fixed">
                            <div className={`flex items-center`}>
                                <span className={`font-sans font-medium text-[13px]`}>Habilitar NF</span>
                            </div>
                        </Tooltip>

                    </div>
                )
            })
        },
        {
            accessorKey: 'acaoNfNaoConfirmada', header: 'Ações', enableSorting: false, size: 100, muiTableBodyCellProps: { align: 'left' }, muiTableHeadCellProps: { align: 'left' },

            Cell: ((cell: any) => {

                const rowData = cell.row.original as RowData;

                const handleConfirmaNfClick = async () => {
                    const result = await confirmarNota(rowData.id);

                    if (result) {
                        await atualizarNfList();
                    }
                }

                const handleBuscarDadosNFClick = async () => {
                    const result = await buscarDadosNF(rowData.id)

                    if (result) {
                        await atualizarNfList();
                    }
                }

                const handleExcluirNF = async () => {
                    const timerExcluir = setTimeout(() => {
                    setIdNF(rowData.id)
                    setNumNF(rowData.num_nf)
                    setModalShow(true)

                    let divPerfil = document.getElementById('div-perfil') as HTMLDivElement

                    if (divPerfil) {
                        divPerfil.classList.add('hidden')
                    }

                    let menuFiltro = document.getElementById("divMenuFiltroGrupo") as HTMLDivElement
                    let menuInputs = document.getElementById("divInputsGrupo") as HTMLDivElement

                    if (menuFiltro) {

                        menuInputs.classList.add("invisible")

                        const timer = setTimeout(() => {
                            menuFiltro.classList.remove("max-h-[390px]")
                            menuFiltro.classList.add("max-h-[0px]")                            
                            clearTimeout(timer)
                        }, 100)
                    }

                    clearTimeout(timerExcluir)
                }, 0);
                }

                return (
                    <div className='flex justify-start w-full'>

                        <button
                            tabIndex={-1}
                            onClick={handleConfirmaNfClick} data-tooltip-id={'tooltip-confirmar-' + rowData.id} id={'btn-confirmar-' + rowData.id}
                            className={`flex items-center justify-center 
                                        text-white transition-all duration-300 
                                        hover:bg-green-600 w-[30px] h-[30px] rounded-md 
                                        bg-green-500 `}
                        >
                            <FaCheck size={'18px'} />
                        </button>
                        <Tooltip id={`tooltip-confirmar-${rowData.id}`} anchorSelect={'#btn-confirmar-' + rowData.id} style={{ backgroundColor: 'rgb(22, 163, 74)' }} place='top' positionStrategy="fixed">
                            <div className={`flex items-center`}>
                                <span className={`font-sans font-medium text-[13px]`}>Confirmar NF</span>
                            </div>
                        </Tooltip>

                        <button
                            tabIndex={-1}
                            onClick={handleBuscarDadosNFClick} data-tooltip-id={'tooltip-editar-' + rowData.id} id={'btn-editar-' + rowData.id}
                            className={`flex items-center justify-center 
                                        text-white transition-all duration-300 
                                        hover:bg-sky-600 w-[30px] h-[30px] rounded-md 
                                        bg-sky-500 ml-2`}>
                            <RiEdit2Fill size={'18px'} />
                        </button>
                        <Tooltip id={`tooltip-editar-${rowData.id}`} anchorSelect={'#btn-editar-' + rowData.id} style={{ backgroundColor: 'rgb(14, 165, 233)' }} place='top-end' positionStrategy="fixed">
                            <div className={`flex items-center`}>
                                <span className={`font-sans font-medium text-[13px]`}>Editar NF</span>
                            </div>
                        </Tooltip>

                        {
                            !rowData.dh_confirm_nf &&
                            <div>
                                <button
                                    tabIndex={-1}
                                    onClick={handleExcluirNF} data-tooltip-id={'tooltip-excluir-' + rowData.id} id={'btn-excluir-' + rowData.id}
                                    className={`flex items-center justify-center 
                                                text-white transition-all duration-300 
                                                hover:bg-red-600 w-[30px] h-[30px] rounded-md 
                                                bg-red-500 ml-2`}>
                                    <FaTrashAlt size={'18px'} />
                                </button>
                                <Tooltip id={`tooltip-excluir-${rowData.id}`} anchorSelect={'#btn-excluir-' + rowData.id} style={{ backgroundColor: 'rgb(239, 68, 68)' }} place='top-end' positionStrategy="fixed">
                                    <div className={`flex items-center`}>
                                        <span className={`font-sans font-medium text-[13px]`}>Excluir NF</span>
                                    </div>
                                </Tooltip>
                            </div>
                        }

                    </div>
                )
            })
        },
        {
            accessorKey: 'id', header: 'NF', size: 50, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },

            Cell: ({ cell }) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.id ? rowData.id : <span className={`text-red-400`}>N/D</span>}</span>)

            }

        },
        {
            accessorKey: 'num_nf', header: 'Número', size: 100, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },

            Cell: ({ cell }) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.num_nf ? rowData.num_nf : <span className={`text-red-400`}>N/D</span>}</span>)

            }
        },
        {
            accessorKey: 'nome_fantasia', header: 'Fornecedor', size: 250, muiTableBodyCellProps: { align: 'left' }, muiTableHeadCellProps: { align: 'left' },
            Cell: ({ cell }) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.nome_fantasia ? rowData.nome_fantasia : <span className={`text-red-400`}>N/D</span>}</span>)

            }
        },
        {
            accessorKey: 'dt_emissao', header: 'Data de Emissão', size: 50, muiTableBodyCellProps: { align: 'center' }, muiTableHeadCellProps: { align: 'center' },
            Cell: ({ cell }) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.dt_emissao ? moment(rowData.dt_emissao).format('DD/MM/YYYY') : <span className={`text-red-400`}>N/D</span>}</span>)

            }
        },
        {
            accessorKey: 'dt_chegada', header: 'Data de Chegada', size: 50, muiTableBodyCellProps: { align: 'center' }, muiTableHeadCellProps: { align: 'center' },
            Cell: ({ cell }) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.dt_chegada ? moment(rowData.dt_chegada).format('DD/MM/YYYY') : <span className={`text-red-400`}>N/D</span>}</span>)

            }

        },
        {
            accessorKey: 'modelo', header: 'Modelo', size: 50, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },
            Cell: ({ cell }) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.modelo ? rowData.modelo : <span className={`text-red-400`}>N/D</span>}</span>)

            }

        },
        {

            accessorKey: 'vl_produtos', header: 'VL. dos Prod. (R$)', size: 100, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },
            Cell: ({ cell }) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.vl_produtos ? Funcoes.numberFormat(rowData.vl_produtos, 'decimal') : <span className={`text-red-400`}>N/D</span>}</span>)

            }

        },
        {
            accessorKey: 'vl_nf', header: 'VL. da NF (R$)', size: 100, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },
            Cell: ({ cell }) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.vl_nf ? Funcoes.numberFormat(rowData.vl_nf, 'decimal') : <span className={`text-red-400`}>N/D</span>}</span>)

            }

        },

    ], [NFList]
    );

    const table = useMaterialReactTable({
        columns: columns,
        data: nfList,
        autoResetPageIndex: false,
        mrtTheme: (theme) => ({
            baseBackgroundColor: baseBackgroundColor,

        }),
        muiTableHeadCellProps: {
            sx: {
                borderBottom: `0.1px solid ${user.modeDark === 'light' ? bgLineGridColorLight : bgLineGridColorDark}`,
                backgroundColor: corBgGrid,
                color: `#fff`,
                '& .MuiTableSortLabel-root': {
                    color: '#fff !important',
                    '&:hover': {
                        color: '#fff !important',
                    },
                    '&.Mui-active': {
                        color: '#fff !important',
                    },
                    '& .MuiTableSortLabel-icon': {
                        color: '#fff !important',
                    },
                    '&.Mui-active .MuiTableSortLabel-icon': {
                        color: '#fff !important',
                    },
                },
                '& .MuiIconButton-root': {
                    color: '#fff',
                    '&:hover': {
                        color: '#fff',
                    },
                },
            },
        },
        muiTableBodyCellProps: {
            sx: {
                borderBottom: `0.1px solid ${user.modeDark === 'light' ? bgLineGridColorLight : bgLineGridColorDark}`
            }
        },
        enableEditing: false,
        enableTopToolbar: false,
        enableColumnFilters: false,
        enableHiding: false,
        enableFilters: false,
        enableMultiSort: false,
        enableFullScreenToggle: false,
        enableDensityToggle: false,
        localization: MRT_Localization_PT_BR,
        paginationDisplayMode: 'pages',
        onColumnVisibilityChange: setColumnVisibility,
        state: { columnVisibility },
        initialState: {
            pagination: { pageIndex: 0, pageSize: 6 },  // Define 6 elementos por página
        },
        muiPaginationProps: {
            rowsPerPageOptions: [6],  // Apenas uma opção para o seletor: 6
        },
    })

    return (
        <>
            <ThemeProvider theme={theme} >

                <MaterialReactTable table={table} />

            </ThemeProvider>

            <ModalExcluirDados
                show={modalShow}
                isLoading={isLoading}
                message={`Deseja excluir a nota fiscal ${numNF || ''}?`}
                alertType={alertType}
                feedBackVisible={feedBackVisible}
                messageFeedBack={messageFeedBack}
                closeModalEvent={fecharModalExcluirDados}
                excluirEvent={handleExcluir}
                fecharModalEvent={buttonFecharModalExcluirDados}
            />

        </>
    )
})


